import React, {useState} from 'react';

import inlineStyleToObject from 'react-html-parser/lib/utils/inlineStyleToObject';

import {useAccountSettingsState} from 'app/settings/api';
import {IMAGE_ERROR_PNG} from 'app/urls';
import {removeMediaPath} from 'app/utils/fineuploader';
import {reportToSentry} from 'app/utils/sentry';
import {getPathFromURL} from 'app/utils/urls';

import {HotlinkWarning} from './HotlinkWarning';
import {ZoomedImage} from './ZoomedImage';

export interface Props {
  className?: string;
  src?: string;
  style?: string;
  localDownloadFailed?: boolean;
  insideLink: boolean;
  mediaURL: string;
  mediaURLRead: string;
  imageSize: 'large' | 'small' | 'original';
}

export function UserContentImage({
  className = '',
  src = '',
  style,
  localDownloadFailed,
  insideLink,
  mediaURL,
  mediaURLRead,
  imageSize,
}: Props): JSX.Element {
  /*
    when error == 'resize' the component couldn't download the resized image
        and will try to fetch the not-resized version from mediaURL
    when error == 'final' the components failed to fetch the not-resized
        version of the image and will show a image with an error message
     */
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState<null | 'resize' | 'final'>(null);
  const [showingModal, showModal] = useState<boolean>(false);

  const uploadEndpoint =
    useAccountSettingsState().data!.s3Upload.IMAGES.requestEndpoint;

  const zoomable = loaded && !insideLink;
  let imageURL = cleanRecentlyUploaded(
    src.replace(/%2F/g, '/'),
    uploadEndpoint,
    mediaURL
  );

  let originalImageURL =
    isMediaImage(imageURL) || isMediaImageOlde(imageURL)
      ? addMediaURLPath(imageURL, mediaURLRead, 'original')
      : imageURL;

  if (error === 'final') {
    imageURL = IMAGE_ERROR_PNG;
  } else {
    if (!/^https?:\/\//.test(imageURL)) {
      // if it's a local image
      imageURL = fixSrcWithLambdaSize(imageURL);
      imageURL = fixSrcWithLambdaSizeForOlde(imageURL);

      if (isMediaImage(imageURL) || isMediaImageOlde(imageURL)) {
        if (error == null) {
          if (isResizable(src)) {
            imageURL = addMediaURLPath(imageURL, mediaURLRead, imageSize);
          } else {
            imageURL = originalImageURL;
          }
        } else {
          imageURL = addMediaURLPath(imageURL, mediaURL);
          originalImageURL = addMediaURLPath(src, mediaURL, 'original');
        }
      }
    }
  }

  function handleError() {
    reportToSentry(
      'Error loading optimized image',
      {
        optimizedURL: imageURL,
        originalURL: src,
      },
      true
    );

    if (error == null && (isMediaImage(src) || isMediaImageOlde(src))) {
      setError('resize');
    } else {
      setError('final');
    }
  }

  const reactStyle: Record<string, any> = {
    cursor: zoomable ? 'zoom-in' : undefined,
    ...(typeof style === 'string' ? inlineStyleToObject(style) : {}),
  };
  // the image width should be applied to the figure tag
  // so the display:inline style can work
  const width = reactStyle['width'];
  delete reactStyle['width'];

  function handleClick(e: React.MouseEvent<unknown>) {
    if (zoomable) {
      e.stopPropagation();
      showModal(true);
    }
  }

  return (
    <figure
      className={className}
      style={{
        width,
      }}
    >
      <div className="image-container">
        <img
          onLoad={() => (error !== 'final' ? setLoaded(true) : null)}
          onClick={handleClick}
          style={reactStyle}
          onError={handleError}
          src={imageURL}
        />
        {localDownloadFailed ? <HotlinkWarning src={src} /> : null}
      </div>
      {zoomable && loaded ? (
        <ZoomedImage
          show={showingModal}
          imageURL={originalImageURL}
          toggle={() => showModal(!showingModal)}
        />
      ) : null}
    </figure>
  );
}

const fixSrcWithLambdaSizeForOlde = (string?: string): string => {
  /* remove hard-coded resize prefix */
  let wrongURL = /\d{1,4}x\d{1,4}(\/|%2F)(\d\ds\d\d)/g;
  return (string || '').replace(wrongURL, '$2');
};

const fixSrcWithLambdaSize = (string?: string): string => {
  /* remove hard-coded resize prefix */
  let wrongURL = /\d{1,4}x\d{1,4}(\/|%2F)(files(%2F|\/)\d*(%2F|\/)\d\ds\d\d)/g;
  return (string || '').replace(wrongURL, '$2');
};

function isMediaImageOlde(url: string): boolean {
  return url.match(/^(%2F|\/)?\d\ds\d\d/) != null;
}

function isMediaImage(url: string): boolean {
  return url.match(/^(%2F|\/)?files(%2F|\/)\d*(%2F|\/)\d\ds\d\d/) != null;
}

function addMediaURLPath(
  basePath: string,
  mediaURL: string,
  size?: 'large' | 'small' | 'original'
) {
  return `${mediaURL}${IMG_SIZES[size || 'original']}${basePath.replace(
    /^(\/|%2F)/,
    ''
  )}`;
}

const IMG_SIZES = {
  large: '1000x1000/',
  small: '500x500/',
  original: '',
};

function isResizable(imageURL: string): boolean {
  return imageURL.match(RESIZABLE_EXTENSIONS_RE) != null;
}

function cleanRecentlyUploaded(
  src: string,
  uploadEndpoint: string,
  mediaURL: string
): string {
  if (src.startsWith(uploadEndpoint)) {
    return removeMediaPath(getPathFromURL(src), mediaURL);
  }

  return src;
}

const RESIZABLE_EXTENSIONS_RE = new RegExp('.(png|jpe?g)$', 'i');
